import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  Plus,
  Trash2,
  Unlock,
} from "lucide-react";
import { gauntlet, ApiError } from "../lib/api";
import { formatDuration, cx } from "../lib/format";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Callout,
  ChainAvatar,
  Input,
  Modal,
  CopyButton,
  Panel,
  Skeleton,
} from "../components/ui";

export function WalletPage() {
  const { walletId } = useParams();
  const id = Number(walletId);
  const queryClient = useQueryClient();

  const [unlocking, setUnlocking] = useState(false);
  const [addingChain, setAddingChain] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [chainFilter, setChainFilter] = useState("");
  const [revealing, setRevealing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["wallets"],
    queryFn: () => gauntlet.wallets(),
    refetchInterval: 20_000,
  });

  const { data: chainData } = useQuery({
    queryKey: ["wallet-chains", id],
    queryFn: () => gauntlet.walletChains(id),
    enabled: addingChain,
  });

  const unlock = useMutation({
    mutationFn: (pw: string) => gauntlet.unlockWallet(id, pw),
    onSuccess: async () => {
      setUnlocking(false);
      setPassword("");
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ["wallets"] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Unlock failed."),
  });

  const lock = useMutation({
    mutationFn: () => gauntlet.lockWallet(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["wallets"] }),
  });

  const addChain = useMutation({
    mutationFn: (chainId: string) => gauntlet.addAccount(id, chainId),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  const wallet = data?.wallets.find((w) => w.id === id);
  if (!wallet) {
    return (
      <Panel>
        <p className="py-10 text-center text-sm text-ink-faint">
          Wallet not found.
        </p>
      </Panel>
    );
  }

  const available = (chainData?.chains ?? []).filter(
    (c) =>
      !c.added &&
      (chainFilter === "" ||
        c.name.toLowerCase().includes(chainFilter.toLowerCase()) ||
        c.symbol.toLowerCase().includes(chainFilter.toLowerCase())),
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight">
              {wallet.name}
            </h1>
            {wallet.type === "watch" && <Badge>watch-only</Badge>}
            {wallet.has_passphrase === 1 && (
              <Badge tone="brass">passphrase</Badge>
            )}
          </div>
          <p className="mt-1 text-xs text-ink-faint">
            {wallet.accounts.length} chain
            {wallet.accounts.length === 1 ? "" : "s"}
            {wallet.fingerprint && ` · seed ${wallet.fingerprint}`}
          </p>
        </div>

        <div className="flex gap-2">
          {wallet.type === "hd" &&
            (wallet.unlocked ? (
              <Button icon={<Lock size={14} />} onClick={() => lock.mutate()}>
                Lock
                {wallet.lockRemainingSec != null && (
                  <span className="num ml-1 text-xs text-ink-faint">
                    {formatDuration(wallet.lockRemainingSec)}
                  </span>
                )}
              </Button>
            ) : (
              <Button
                variant="primary"
                icon={<Unlock size={14} />}
                onClick={() => setUnlocking(true)}
              >
                Unlock
              </Button>
            ))}
          {/* Only HD wallets can derive a new account. A watch-only wallet
              tracks one imported xpub and has no seed, so `unlocked` is false
              forever — offering this would open a modal of dead buttons telling
              the user to unlock something that has no unlock control. */}
          {wallet.type === "hd" && (
            <Button
              icon={<Plus size={14} />}
              onClick={() => setAddingChain(true)}
            >
              Add chain
            </Button>
          )}
        </div>
      </div>

      {!wallet.unlocked && wallet.type === "hd" && (
        <Callout tone="info" title="This wallet is locked">
          You can still view addresses and balances. Unlocking is only needed to
          derive new addresses or sign a transaction.
        </Callout>
      )}

      <Panel title="Chains" padded={false}>
        <ul className="divide-y divide-hairline">
          {wallet.accounts.map((account) => (
            <li
              key={account.id}
              className="flex items-center gap-3 px-5 py-3.5"
            >
              <ChainAvatar
                chainId={account.chain_id}
                symbol={account.chain_id.slice(0, 3)}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium capitalize">
                  {account.chain_id.replace(/-/g, " ")}
                </p>
                <p className="addr text-ink-faint">{account.derivation_path}</p>
              </div>
              <div className="flex gap-1.5">
                <Link to={`/accounts/${account.id}/receive`}>
                  <Button size="sm" icon={<ArrowDownLeft size={13} />}>
                    Receive
                  </Button>
                </Link>
                <Link to={`/accounts/${account.id}/send`}>
                  <Button size="sm" icon={<ArrowUpRight size={13} />}>
                    Send
                  </Button>
                </Link>
              </div>
            </li>
          ))}
        </ul>
      </Panel>

      {/* Unlock */}
      <Modal
        open={unlocking}
        onClose={() => setUnlocking(false)}
        title="Unlock wallet"
      >
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            unlock.mutate(password);
          }}
        >
          <Input
            label="Vault password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            autoFocus
            error={error}
          />
          <p className="text-xs text-ink-faint">
            This is the password you set when creating the wallet, not your
            sign-in password.
          </p>
          <div className="flex gap-2">
            <Button full type="button" onClick={() => setUnlocking(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              full
              type="submit"
              loading={unlock.isPending}
            >
              Unlock
            </Button>
          </div>
        </form>
      </Modal>

      {/* Add chain */}
      {/* Backup and removal. The phrase used to be shown exactly once at
          creation and never again, even though the server could always
          re-issue it behind the vault password. */}
      <Panel title="Backup and removal">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Recovery phrase</p>
              <p className="text-xs text-ink-faint">
                Shows the words again, behind your vault password. Anyone who
                reads them can take everything in this wallet.
              </p>
            </div>
            <Button
              icon={<KeyRound size={14} />}
              onClick={() => setRevealing(true)}
            >
              Reveal
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-3 border-t border-hairline pt-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-loss">Delete this wallet</p>
              <p className="text-xs text-ink-faint">
                Removes the encrypted seed and every derived address from this
                server. The coins stay on their chains — but without the phrase
                written down, they become unreachable.
              </p>
            </div>
            <Button
              variant="danger"
              icon={<Trash2 size={14} />}
              onClick={() => setDeleting(true)}
            >
              Delete
            </Button>
          </div>
        </div>
      </Panel>

      <Modal
        open={addingChain}
        onClose={() => setAddingChain(false)}
        title="Add a chain"
        width="max-w-lg"
      >
        <div className="space-y-3">
          <Input
            placeholder="Search 130+ chains…"
            value={chainFilter}
            onChange={(e) => setChainFilter(e.target.value)}
            autoFocus
          />
          <ul className="max-h-80 divide-y divide-hairline overflow-y-auto rounded-lg border border-hairline">
            {available.slice(0, 60).map((chain) => (
              <li
                key={chain.id}
                className="flex items-center gap-3 px-3 py-2.5"
              >
                <ChainAvatar
                  chainId={chain.id}
                  symbol={chain.symbol}
                  size={26}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{chain.name}</p>
                  <p className="text-xs text-ink-faint">{chain.symbol}</p>
                </div>
                {/* Be honest about what each tier can actually do. */}
                <Badge
                  tone={
                    chain.tier === "full"
                      ? "gain"
                      : chain.tier === "watch"
                        ? "brass"
                        : "neutral"
                  }
                >
                  {chain.tier === "full"
                    ? "send + receive"
                    : chain.tier === "watch"
                      ? "receive only"
                      : "address only"}
                </Badge>
                <Button
                  size="sm"
                  onClick={() => {
                    // Route a locked wallet to the unlock dialog rather than
                    // leaving the control disabled with nothing to click.
                    if (!wallet.unlocked) {
                      setAddingChain(false);
                      setUnlocking(true);
                      return;
                    }
                    addChain.mutate(chain.id);
                  }}
                  loading={
                    addChain.isPending && addChain.variables === chain.id
                  }
                >
                  {wallet.unlocked ? "Add" : "Unlock"}
                </Button>
              </li>
            ))}
            {available.length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-ink-faint">
                No matching chains.
              </li>
            )}
          </ul>
          {!wallet.unlocked && (
            <Callout tone="warn">
              Unlock the wallet first — adding a chain derives new keys.
            </Callout>
          )}
        </div>
      </Modal>

      <RevealPhraseModal
        walletId={id}
        open={revealing}
        onClose={() => setRevealing(false)}
      />
      <DeleteWalletModal
        walletId={id}
        walletName={wallet.name}
        open={deleting}
        onClose={() => setDeleting(false)}
      />
    </div>
  );
}


/** Re-reveals the recovery phrase. Gated on the vault password every time. */
function RevealPhraseModal({
  walletId,
  open,
  onClose,
}: {
  walletId: number;
  open: boolean;
  onClose: () => void;
}) {
  const [password, setPassword] = useState("");
  const [phrase, setPhrase] = useState<string | null>(null);
  const [passphrase, setPassphrase] = useState<string | undefined>();
  const [shown, setShown] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reveal = useMutation({
    mutationFn: () => gauntlet.exportWallet(walletId, password),
    onSuccess: (r) => {
      setPhrase(r.mnemonic);
      setPassphrase(r.passphrase);
      setError(null);
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : "That password was not accepted."),
  });

  const close = () => {
    setPassword("");
    setPhrase(null);
    setPassphrase(undefined);
    setShown(false);
    setError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={close} title="Recovery phrase" width="max-w-lg">
      {phrase === null ? (
        <div className="space-y-3">
          <Callout tone="warn" title="Make sure nobody can see your screen">
            This displays the words that control every coin in this wallet.
          </Callout>
          <Input
            label="Vault password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            autoComplete="current-password"
          />
          {error && <Callout tone="danger">{error}</Callout>}
          <Button
            variant="primary"
            full
            disabled={!password}
            loading={reveal.isPending}
            onClick={() => reveal.mutate()}
          >
            Reveal phrase
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="relative">
            <ol
              className={cx(
                "grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-lg border border-hairline bg-abyss p-4 sm:grid-cols-3",
                !shown && "select-none blur-sm",
              )}
            >
              {phrase.split(" ").map((word, index) => (
                <li key={index} className="flex items-baseline gap-2 text-sm">
                  <span className="num w-5 shrink-0 text-right text-xs text-ink-faint">
                    {index + 1}
                  </span>
                  <span className="font-mono">{word}</span>
                </li>
              ))}
            </ol>
            {!shown && (
              <button
                onClick={() => setShown(true)}
                className="absolute inset-0 flex items-center justify-center gap-2 text-sm font-medium text-brass"
              >
                <Eye size={16} />
                Tap to reveal
              </button>
            )}
          </div>

          {passphrase && (
            <Callout tone="warn" title="This wallet also has a BIP-39 passphrase">
              The words alone will not restore it. Passphrase:{" "}
              <span className="addr">{shown ? passphrase : "••••••••"}</span>
            </Callout>
          )}

          <div className="flex items-center justify-between">
            <button
              onClick={() => setShown((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs text-ink-dim hover:text-ink"
            >
              {shown ? <EyeOff size={13} /> : <Eye size={13} />}
              {shown ? "Hide" : "Reveal"}
            </button>
            {shown && <CopyButton value={phrase} label="Copy phrase" />}
          </div>

          <Button variant="primary" full onClick={close}>
            Done
          </Button>
        </div>
      )}
    </Modal>
  );
}

/** Wallet deletion. Typing the name is the confirmation, not a checkbox. */
function DeleteWalletModal({
  walletId,
  walletName,
  open,
  onClose,
}: {
  walletId: number;
  walletName: string;
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);

  const destroy = useMutation({
    mutationFn: () => gauntlet.deleteWallet(walletId, password),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onClose();
      void navigate("/");
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : "Could not delete the wallet."),
  });

  return (
    <Modal open={open} onClose={onClose} title="Delete this wallet">
      <div className="space-y-3">
        <Callout tone="danger" title="This cannot be undone">
          The encrypted seed is erased from this server. If you have not written
          the recovery phrase down somewhere else, any coins in this wallet are
          gone permanently — Gauntlet cannot recover them and neither can anyone
          else.
        </Callout>
        <Input
          label={`Type "${walletName}" to confirm`}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
        <Input
          label="Vault password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
        />
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="flex gap-2">
          <Button full onClick={onClose}>
            Keep it
          </Button>
          <Button
            variant="danger"
            full
            disabled={typed !== walletName || !password}
            loading={destroy.isPending}
            onClick={() => destroy.mutate()}
          >
            Delete forever
          </Button>
        </div>
      </div>
    </Modal>
  );
}
